import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, UserPlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API, errorText } from "@/lib/payments";

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed. Select the text and copy it manually.");
  }
};

export const TeamCard = ({ onLegacyLoaded }) => {
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/team`);
      setTeam(data);
      onLegacyLoaded(data);
    } catch (err) {
      toast.error(errorText(err, "Could not load team"));
    }
  }, [onLegacyLoaded]);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/invites`, { email });
      setLink({ ...data, url: `${window.location.origin}${data.setup_path}` });
      setEmail("");
      load();
    } catch (err) {
      toast.error(errorText(err, "Could not create invite"));
    } finally {
      setBusy(false);
    }
  };

  const post = async (url, msg, method = "post") => {
    if (url.includes("/deactivate") && !window.confirm("Remove this person's admin access? They will be signed out immediately.")) return;
    try {
      await axios[method](`${API}${url}`);
      toast.success(msg);
      if (method === "delete") setLink(null);
      load();
    } catch (err) {
      toast.error(errorText(err, "Action failed"));
    }
  };

  return (
    <Card data-testid="team-card">
      <CardHeader><CardTitle className="font-heading text-lg">Team members</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {!team ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
          <div className="divide-y divide-stone-100">
            {team.users.map((u) => (
              <div key={u.id} className="py-3 flex items-center justify-between gap-3" data-testid={`team-user-${u.email}`}>
                <div><p className="text-sm font-medium text-stone-900">{u.name} <span className="text-stone-500 font-normal">{u.email}</span></p>
                  <p className="text-xs text-stone-500">Last sign-in: {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}</p></div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{u.role}</Badge>
                  {u.status !== "active" ? <Badge className="bg-stone-100 text-stone-600">disabled</Badge>
                    : u.role !== "owner" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => post(`/auth/team/${u.id}/deactivate`, "Access removed")} data-testid={`team-deactivate-btn-${u.email}`}>Remove access</Button>}
                </div>
              </div>
            ))}
            {team.invites.map((i) => (
              <div key={i.id} className="py-3 flex items-center justify-between" data-testid={`team-invite-${i.email}`}>
                <p className="text-sm text-stone-600">{i.email} <span className="text-xs text-stone-400">invite pending until {new Date(i.expires_at).toLocaleString()}</span></p>
                <Button size="sm" variant="ghost" onClick={() => post(`/auth/invites/${i.id}`, "Invite revoked", "delete")} data-testid={`team-revoke-invite-btn-${i.email}`}>Revoke</Button>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={invite} className="flex gap-2">
          <Input type="email" required placeholder="teammate@prithiraglobal.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email-input" />
          <Button type="submit" disabled={busy} data-testid="team-invite-btn"><UserPlus className="h-4 w-4 mr-1" />Invite</Button>
        </form>
        {link && (
          <div className="bg-amber-50 rounded-lg p-3 space-y-2 text-sm" data-testid="team-invite-link-panel">
            <p>Send this one-time link privately to <b>{link.email}</b>. It expires {new Date(link.expires_at).toLocaleString()} and is shown only once.</p>
            <p className="font-mono text-xs break-all" data-testid="team-invite-link">{link.url}</p>
            <Button size="sm" variant="outline" onClick={() => copyText(link.url, "Invite link")} data-testid="team-invite-copy-btn"><Copy className="h-4 w-4 mr-1" />Copy link</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
